import React, { useState, useEffect } from 'react';
import BasePage from '../components/BasePage';
import FieldsGroup from '../components/FieldsGroup';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import DocumentButton from '../components/Buttons/DocumentButton';
import { Download, BadgePlus } from 'lucide-react';
import { faCircleArrowLeft } from '@fortawesome/free-solid-svg-icons';

const Contrat = (props) => {
    const [contratSelectionne, setContratSelectionne] = useState(null);
    const [dataFromBasePage, setDataFromBasePage] = useState(null);

    useEffect(() => {
        if (dataFromBasePage && !contratSelectionne) {
            setContratSelectionne(dataFromBasePage);
        }
    }, [dataFromBasePage]);

    const fields_groups = [
        {
            key: 'project',
            name: 'Mon projet',
            icon: 'fa-globe fa-solid',
            fields: [
                {
                    key: 'ndd',
                    name: '',
                    single_key: 'url',
                    format: 'url'
                }, {
                    key: 'type_site',
                    name: 'Type de site'
                },
                {
                    key: 'etat_id',
                    name: 'État',
                    invert: 1
                }, {
                    key: 'reconduction',
                    name: 'Reconduction',
                    format: 'bool',
                    invert: 1
                },
                {
                    key: 'date_signature',
                    name: 'Date de signature',
                    format: 'date'
                }, 
                {
                    key: 'date_signature_pv',
                    name: 'Date de signature PV',
                    format: 'date'
                },
                {
                    key: 'user',
                    name: 'Collaborateur',
                    format: 'user'
                }
            ]
        }, {
            key: 'money',
            name: 'Mon loyer',
            icon: 'fa-regular fa-credit-card',
            fields: [
                {
                    key: 'mensualite_ht',
                    name: 'Mensualité',
                    format: 'money',
                    suffix: 'HT'
                }, {
                    key: 'date_premier_prelevement',
                    name: 'Date du 1er prélèvement',
                    format: 'date'
                }, {
                    key: 'date_dernier_prelevement',
                    name: 'Date de dernier prélèvement',
                    format: 'date'
                }, {
                    key: 'date_fin_contrat',
                    name: 'Date de fin de contrat initiale',
                    format: 'date'
                }, {
                    key: 'date_fin_contrat_tacite',
                    name: 'Date de fin de contrat tacite',
                    format: 'date'
                },
                {
                    key: 'frais_adhesion_ht',
                    name: 'Frais de dossier',
                    format: 'money'
                }
            ]
        }, {
            key: 'leaser',
            name: 'Mon partenaire financier',
            icon: 'fa-suitcase fa-solid',
            fields: [
                {
                    key: 'name',
                    name: 'Nom'
                }, {
                    key: 'adresse_leaser',
                    name: 'Adresse',
                }, {
                    key: 'tel_espace_client',
                    name: 'N° téléphone',
                }, {
                    key: 'email_espace_client',
                    name: 'E-mail'
                }, {
                    key: 'ics',
                    name: 'ICS',
                }, {
                    key: 'siren',
                    name: 'SIREN',
                }
            ]
        }
    ]

    const infosPackLabels = {
        heures_dispo: "Support technique et modifications",
        nb_pages: "Nombre de pages",
        creation_reseau: "Création réseaux sociaux",
        suivi_client: "Suivi client",
        redac_contenu: "Rédaction ou optimisation du contenu",
        budget_sea: "Budget campagne Adwords SEA",
        nb_produits: "Nombre de produits",
        creation_produit: "Création produits",
        stockage: "Stockage",
        categ_seo: "Optimisation SEO de catégories",
        nb_mot_cle: "Nombre de mots clés",
        seo_booster_ia: "SEO Booster IA",
        social_media_poster: "Social Media Poster",
        click_collect: "Click & collect",
        categ_seo_bool: "Catégories SEO activées",
        panier_abandonne: "Module relance panier abandonné",
        carte_bancaire: "Module carte bancaire",
        has_video: "Vidéo d'entreprise 30 sec + 10 photos",
        outil_quote: "Outil Quote"
    };

  const content = (data) => {
    if (!dataFromBasePage) setDataFromBasePage(data);
    if (!contratSelectionne) return null;

    const contratActuel = contratSelectionne;

    const hasPack =
        Number(contratActuel.type_pack) > 0
        || contratActuel.pack_contents
        || contratActuel.projet_id === "2";

    if (!dataFromBasePage) setDataFromBasePage(data);
        if (!contratSelectionne) return null;

        const formatValue = (key, value) => {
            switch (key) {
                case "heures_dispo":
                    return value === "-1" ? "Illimitées" : `${value} h`;
                case "nb_pages":
                    return value === "-1" ? "Illimitées" : value; 
                case "creation_reseau":
                    return `Jusqu'à ${value}`; 
                case "suivi_client":
                    return `Personnalisé (${value} mois)`; 
                case "redac_contenu":
                    return `${value} pages`;
                case "seo_booster_ia":
                    return `${value} articles / mois`; 
                case "social_media_poster":
                    return null; 
                case "click_collect":
                    return null; 
                case "carte_bancaire":
                    return  null; 
                case "panier_abandonne":
                    return null; 
                case "outil_quote":
                    return null; 
                case "has_video":
                    return null; 
                case "stockage":
                    return `${value} GO`;
                case "budget_sea":
                    return `${value} €`;
                default:
                    return value; // Par défaut, valeur brute
            }
        };

        // Choix du contenu en fonction du type de contrat
        let inclusions = [];
        let inclusionTitle = "";

        if (data.projet_id === "2") { // DIGILIX
            inclusionTitle = "Votre pack Digilix inclut :";
            inclusions = [
                "Design prédéfini",
                "Nom de domaine inclus",
                "Hébergement sécurisé",
                "Compatible mobile et tablette",
                "Fiche Google Business",
                "Mises à jour illimitées 24 / 7",
                "Statistiques de performance",
                "Jusqu'à 20 pages",
                "1 réseau social créé",
                "1 modification annuelle",
                "Indexation Google",
                "SEO sur Nom de marque",
                "Maillage interne",
                "Optimisation de 10 pages"
            ];
        } else if (data.projet_id === "1" && data.type_site === "vitrine" && data.type_pack === "0") { // VITRINE CLASSIQUE
                inclusionTitle = "Votre contrat vitrine inclut :";
                inclusions = [
                    "Site internet sur-mesure",
                    "Hébergement sécurisé en France",
                    "Optimisation mobile et tablette",
                    "Création ou optimisation fiche GMB",
                    "Mises à jour illimitées 24/7 (Jerico)",
                    "Accès aux statistiques de performance",
                    "Support technique et modifications",
                    "Suivi client",
                    "Indexation Google",
                    "Optimisation SEO - Nom de la marque",
                    "Maillage interne optimisé",
                    "Rédaction ou optimisation du contenu"
                ];
        } else if (data.projet_id === "1" && data.type_site === "ecommerce" && data.type_pack === "0") { // ECOMMERCE CLASSIQUE
                inclusionTitle = "Votre contrat ecommerce inclut :";
                inclusions = [
                    "Site internet sur-mesure",
                    "Hébergement sécurisé en France",
                    "Optimisation mobile et tablette",
                    "Création ou optimisation fiche GMB",
                    "Mises à jour illimitées 24/7 (Jerico)",
                    "Accès aux statistiques de performance",
                    "Support technique et modifications",
                    "Suivi client",
                    "Indexation Google",
                    "Optimisation SEO - Nom de la marque",
                    "Maillage interne optimisé",
                    "Rédaction ou optimisation du contenu"
                ];
        }

        return (
            <div id="contrat" className="">
                <div className="bloc-info p-10 mb-8 mt-8 flex flex-col lg:flex-row">
                    <div
                        className="flex flex-col lg:w-1/3 w-full cursor-pointer"
                        onClick={() => setContratSelectionne(dataFromBasePage)}
                    >
                        <div className="font-bold text-lg">
                            <FontAwesomeIcon className="text-mlime text-3xl pr-2" icon="fa-solid fa-folder" />
                            N° dossier
                        </div>
                        <div>{contratActuel.num_dossier}</div>
                    </div>
                    {data.ancien_contrat && data.ancien_contrat.length > 0 && (
                        <div className="flex flex-wrap gap-6 mt-4">
                            {data.ancien_contrat.map((contrat, index) => {
                                const isSelected = contrat === contratSelectionne;
                                return (
                                    <div key={index} className="flex flex-col">
                                        <span className="text-xs italic text-gray-500 mb-1">Ancien contrat</span>
                                        <button
                                            onClick={() => setContratSelectionne(contrat)}
                                            className={`text-sm px-4 py-3 rounded shadow min-w-[200px] text-left
                                                ${isSelected ? 'bg-mlime/20 ring-2 ring-mlime text-mdark font-semibold' : 'bg-gray-100 hover:bg-gray-200'}`}
                                        >
                                            <FontAwesomeIcon className="text-mlime text-xl pr-2" icon="fa-solid fa-folder" />
                                            {contrat.num_dossier}
                                        </button>
                                    </div>
                                );
                            })}
                            {contratActuel !== dataFromBasePage && (
                                <button
                                    onClick={() => setContratSelectionne(dataFromBasePage)}
                                    className=" button bg-mlime w-full lg:w-auto px-5 py-2 my-4 rounded-3xl duration-500 hover:bg-colorbutton text-white"
                                >
                                    <FontAwesomeIcon icon={faCircleArrowLeft} className="mr-2" />
                                    Revenir au contrat en cours
                                </button>
                            )}
                        </div>
                    )}
                </div>

                <FieldsGroup fields_groups={fields_groups} data={contratActuel} show_icons={false}/>

                {/* Bloc du document du contrat */}
                {contratActuel.docs.cdc && (
                    <div className="text-center">
                        <DocumentButton
                            filename={contratActuel.docs.cdc.url}
                            file_type="signature"
                            buttonTitle="Mon Contrat"
                            className="bg-mlime px-8 py-5 rounded-3xl max-w-lg duration-500 hover:bg-colorbutton"
                            icon={<Download className="text-white" />}
                            loader={{ size: '60%', firstColor: 'white', secondColor: '#eeeeee88' }}
                        />
                    </div>
                )}

            {/*Section options du contrat*/}
            {contratSelectionne === dataFromBasePage && (
            <>
                <div className='bloc-info p-8 mt-8 flex flex-col lg:w-3/5 m-auto text-sm sm:w-11/12"'>
                    <h2 className="text-lg font-bold uppercase bold inline-flex mb-4">
                        <BadgePlus className='text-[#8cc83c] mr-4 mb-2'/>
                        {hasPack ? 'Contenu de votre pack' : inclusionTitle}
                    </h2>

                    {hasPack ? (
                        <>
                        {/* Si c'est un pack */}
                        {contratActuel.pack_contents && Object.entries(contratActuel.pack_contents).map(([section, items]) => (
                            <div key={section} className="mb-6">
                                <h3 className="font-bold text-mdark text-[16px] mb-2">{section}</h3>
                                <ul className="grid grid-cols-1 md:grid-cols-2 gap-1 text-sm">
                                    {items.map((item, i) => (
                                    <li key={i} className="flex items-start">
                                        <FontAwesomeIcon icon="fa-solid fa-circle-check" className="text-[#8cc83c] w-4 h-4 mt-0.5 mr-2"/>
                                        <span>{item}</span>
                                    </li>
                                    ))}
                                </ul>
                            </div>
                        ))}

                        {/* Options personnalisées du pack */}
                        {contratActuel.infos_pack && (
                            <div className="mt-2">
                                <h3 className="font-bold text-mdark text-[16px] mb-2">Options</h3>
                                <ul className="grid grid-cols-1 md:grid-cols-2 gap-1 text-sm">
                                    {Object.entries(contratActuel.infos_pack).map(([key, value]) => {
                                    if (value === null || value === "" || value === "0") return null;
                                    const formatted = formatValue(key, value);
                                    return (
                                        <li key={key} className="flex items-start">
                                            <FontAwesomeIcon icon="fa-solid fa-circle-check" className="text-[#8cc83c] w-4 h-4 mt-0.5 mr-2"/>
                                            <span>
                                                {infosPackLabels[key] || key}
                                                {formatted ? ` : ${formatted}` : ""}
                                            </span>
                                        </li>
                                    );
                                    })}
                                </ul>
                            </div>
                        )}

                        {/* Si c'est un digilix */}
                        {!contratActuel.pack_contents && contratActuel.projet_id === "2" && (
                            <ul className="grid grid-cols-1 md:grid-cols-2 gap-1 text-sm">
                            {[
                                "Design prédéfini","Nom de domaine inclus","Hébergement sécurisé","Compatible mobile et tablette",
                                "Fiche Google Business","Mises à jour illimitées 24/7","Statistiques de performance","Jusqu'à 20 pages",
                                "1 réseau social créé","1 modification annuelle","Indexation Google","SEO sur Nom de marque",
                                "Maillage interne","Optimisation de 10 pages",
                            ].map((txt,i)=>(
                                <li key={i} className="flex items-start">
                                    <FontAwesomeIcon icon="fa-solid fa-circle-check" className="text-[#8cc83c] w-4 h-4 mt-0.5 mr-2"/>
                                    <span>{txt}</span>
                                </li>
                            ))}
                            </ul>
                        )}
                        </>
                    ) : (
                        // Si c'est un ancien contrat
                        <ul className="grid grid-cols-1 md:grid-cols-2 gap-1 text-sm">
                            {inclusions.map((item, idx) => (
                                <li key={idx} className="flex items-center">
                                <FontAwesomeIcon icon="fa-solid fa-circle-check" className="text-[#8cc83c] w-4 h-4 mt-1 mr-2"/>
                                {item}
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </>
            )}
        </div>
        );
    };
    return <BasePage content={content} title={props.title ?? null} />
}

export default Contrat;